import { useMemo, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { PLANS } from "@/data";

export default function Pricing({ onApply }) {
  const [windows, setWindows] = useState(18);
  const [floorWindows, setFloorWindows] = useState(2);
  const [dachWindows, setDachWindows] = useState(0);
  const [wintergartenQm, setWintergartenQm] = useState(0);
  const [panoramaQm, setPanoramaQm] = useState(0);
  const reduce = useReducedMotion();

  const estimate = useMemo(
    () => Math.max(50, windows * 6 + floorWindows * 9 + dachWindows * 9 + wintergartenQm * 7 + panoramaQm * 4),
    [windows, floorWindows, dachWindows, wintergartenQm, panoramaQm]
  );

  const apply = () => {
    onApply(
      `Geschätzter Umfang: ca. ${windows} normale Fenster, ${floorWindows} bodentiefe Fenster/Türen, ${dachWindows} Dachfenster, Wintergarten/Glasdach ca. ${wintergartenQm} m², Panoramafenster ca. ${panoramaQm} m² (Richtwert ca. ${estimate.toLocaleString("de-DE", {
        minimumFractionDigits: 2,
      })} €).\n\nMeine Nachricht: `
    );
  };

  return (
    <section id="preise" data-testid="pricing-section" className="py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 max-w-2xl">
          <span className="inline-block rounded-full border border-sky-200/70 bg-sky-50/80 px-3 py-1 font-mono text-xs uppercase tracking-[0.25em] text-sky-800">
            Preise
          </span>
          <h2 className="mt-4 font-serif text-3xl font-medium tracking-tight text-slate-900 sm:text-4xl">
            Festpreise. <em className="italic text-sky-700">Keine Überraschungen.</em>
          </h2>
        </div>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
          {PLANS.map((p, i) => (
            <motion.div
              key={p.id}
              data-testid={`pricing-plan-${p.id}`}
              initial={{ opacity: 0, y: reduce ? 0 : 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className={`relative flex flex-col rounded-2xl border p-8 ${
                p.featured
                  ? "border-sky-700 bg-slate-900 text-white shadow-[0_30px_60px_-25px_rgba(15,23,42,0.5)]"
                  : "border-slate-200 bg-white"
              }`}
            >
              {p.featured && (
                <span className="absolute -top-3 left-8 rounded-full bg-sky-700 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-white">
                  Beliebt bei Gewerbe
                </span>
              )}
              <h3 className={`font-serif text-xl font-medium ${p.featured ? "text-white" : "text-slate-900"}`}>{p.name}</h3>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="font-serif text-4xl font-semibold tracking-tight">{p.price}</span>
                <span className={`font-mono text-xs uppercase tracking-wider ${p.featured ? "text-slate-400" : "text-slate-500"}`}>
                  {p.unit}
                </span>
              </div>
              <ul className="mt-6 flex-1 space-y-3">
                {p.features.map((f) => (
                  <li key={f} className={`flex items-start gap-3 text-sm ${p.featured ? "text-slate-300" : "text-slate-600"}`}>
                    <Check className={`mt-0.5 h-4 w-4 shrink-0 ${p.featured ? "text-sky-400" : "text-teal-600"}`} />
                    {f}
                  </li>
                ))}
              </ul>
              <a
                href="#kontakt"
                data-testid={p.testId}
                className={`mt-8 block rounded-full px-5 py-3 text-center text-sm font-semibold transition-colors duration-200 ${
                  p.featured
                    ? "bg-sky-600 text-white hover:bg-sky-500"
                    : "border border-slate-300 text-slate-800 hover:border-sky-400 hover:text-sky-700"
                }`}
              >
                Angebot anfordern
              </a>
            </motion.div>
          ))}
        </div>

        <p data-testid="pricing-vat-note" className="mt-8 text-center text-sm text-slate-500">
          Alle Preise verstehen sich ohne Umsatzsteuer – gemäß § 19 UStG (Kleinunternehmerregelung) wird keine
          Umsatzsteuer berechnet. Anfahrt innerhalb von Witten: 5 €.
        </p>

        <motion.div
          initial={{ opacity: 0, y: reduce ? 0 : 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          data-testid="pricing-calculator"
          id="preisschaeizer"
          className="mt-16 rounded-2xl border border-slate-200 bg-white p-8 sm:p-10"
        >
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
            <div>
              <h3 className="font-serif text-2xl font-medium text-slate-900">Preisschätzer für Ihr Zuhause</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                Wie viele Fenster sollen gereinigt werden? Der Richtwert basiert auf 6 € pro Fenster (beidseitig,
                inklusive Rahmen und Fensterbänke), 9 € für bodentiefe Fenster/Türen und Dachfenster, 7 € pro m²
                Wintergarten/Glasdach und 4 € pro m² Panoramafenster – zzgl. 5 € Anfahrt innerhalb von Witten.
                Der tatsächliche Preis kann bei schwerer Erreichbarkeit und starker Verschmutzung variieren.
              </p>
              <div className="mt-8">
                <div className="mb-3 flex items-center justify-between">
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500">Normale Fenster (6 € pro Stück)</span>
                  <span data-testid="pricing-window-count" className="font-serif text-2xl font-semibold text-slate-900">
                    {windows}
                  </span>
                </div>
                <Slider
                  data-testid="pricing-calculator-slider"
                  value={[windows]}
                  onValueChange={([v]) => setWindows(v)}
                  min={5}
                  max={60}
                  step={1}
                  aria-label="Anzahl Fenster"
                />
                <div className="mt-2 flex justify-between font-mono text-[10px] uppercase tracking-wider text-slate-400">
                  <span>5</span>
                  <span>60</span>
                </div>
              </div>
              <div className="mt-6">
                <div className="mb-3 flex items-center justify-between">
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500">
                    Bodentiefe Fenster/Türen (9 €)
                  </span>
                  <span data-testid="pricing-floorwindow-count" className="font-serif text-2xl font-semibold text-slate-900">
                    {floorWindows}
                  </span>
                </div>
                <Slider
                  data-testid="pricing-floorwindow-slider"
                  value={[floorWindows]}
                  onValueChange={([v]) => setFloorWindows(v)}
                  min={0}
                  max={20}
                  step={1}
                  aria-label="Anzahl bodentiefe Fenster oder Türen"
                />
                <div className="mt-2 flex justify-between font-mono text-[10px] uppercase tracking-wider text-slate-400">
                  <span>0</span>
                  <span>20</span>
                </div>
              </div>
              <div className="mt-6">
                <div className="mb-3 flex items-center justify-between">
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500">Dachfenster (9 € pro Stück)</span>
                  <span data-testid="pricing-dachfenster-count" className="font-serif text-2xl font-semibold text-slate-900">{dachWindows}</span>
                </div>
                <Slider
                  data-testid="pricing-dachfenster-slider"
                  value={[dachWindows]}
                  onValueChange={([v]) => setDachWindows(v)}
                  min={0}
                  max={20}
                  step={1}
                  aria-label="Anzahl Dachfenster"
                />
                <div className="mt-2 flex justify-between font-mono text-[10px] uppercase tracking-wider text-slate-400">
                  <span>0</span>
                  <span>20</span>
                </div>
              </div>
              <div className="mt-6">
                <div className="mb-3 flex items-center justify-between">
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500">Wintergarten/Glasdach (7 € pro m²)</span>
                  <span data-testid="pricing-wintergarten-count" className="font-serif text-2xl font-semibold text-slate-900">{wintergartenQm} m²</span>
                </div>
                <Slider
                  data-testid="pricing-wintergarten-slider"
                  value={[wintergartenQm]}
                  onValueChange={([v]) => setWintergartenQm(v)}
                  min={0}
                  max={50}
                  step={1}
                  aria-label="Quadratmeter Wintergarten oder Glasdach"
                />
                <div className="mt-2 flex justify-between font-mono text-[10px] uppercase tracking-wider text-slate-400">
                  <span>0</span>
                  <span>50</span>
                </div>
              </div>
              <div className="mt-6">
                <div className="mb-3 flex items-center justify-between">
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500">Panoramafenster (4 € pro m²)</span>
                  <span data-testid="pricing-panorama-count" className="font-serif text-2xl font-semibold text-slate-900">{panoramaQm} m²</span>
                </div>
                <Slider
                  data-testid="pricing-panorama-slider"
                  value={[panoramaQm]}
                  onValueChange={([v]) => setPanoramaQm(v)}
                  min={0}
                  max={40}
                  step={1}
                  aria-label="Quadratmeter Panoramafenster"
                />
                <div className="mt-2 flex justify-between font-mono text-[10px] uppercase tracking-wider text-slate-400">
                  <span>0</span>
                  <span>40</span>
                </div>
              </div>
            </div>
            <div className="rounded-2xl bg-slate-50 p-8 text-center">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500">Ihr unverbindlicher Richtwert</p>
              <p data-testid="pricing-estimate" className="mt-3 font-serif text-5xl font-semibold tracking-tight text-sky-700">
                {estimate.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €
              </p>
              <p className="mt-2 text-xs text-slate-500">Richtwert ohne Umsatzsteuer (§ 19 UStG) – verbindlicher Festpreis nach Besichtigung</p>
              <button
                onClick={apply}
                data-testid="pricing-calculator-apply"
                className="group mt-6 inline-flex items-center gap-2 rounded-full bg-slate-900 px-6 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-sky-700"
              >
                Schätzung in Anfrage übernehmen
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
